/** Deployment configuration: storage, queue, search. */
const en = {
  title: 'Configuration',
  needAdmin: 'You need an admin or owner role to manage deployment configuration.',
  subtitle:
    'Where ReplayOps stores and processes data. Changes are saved to the database and applied when the services restart.',
  restartPending:
    'Saved settings differ from the running services. Restart the API, collector and workers to apply them.',
  testFailed: 'Test request failed',
  storage: {
    title: 'Session storage',
    subtitle: 'Where recorded session events are kept.',
    driver: 'Driver',
    s3: 'S3 / S3-compatible (AWS, MinIO, Ceph…)',
    local: 'Local disk (on-prem / air-gapped)',
    preset: 'Provider preset',
    presetHelp: 'Pre-fills the settings that differ between S3-compatible stores.',
    presetAws: 'AWS S3',
    presetMinio: 'MinIO',
    presetCeph: 'Ceph (RadosGW)',
    presetOther: 'Other S3-compatible',
    pathStyle: 'Path-style addressing',
    pathStyleHelp: 'AWS: virtual-host · MinIO/Ceph: path-style',
    pathAuto: 'Auto (endpoint → path-style)',
    pathOn: 'Force on (path-style)',
    pathOff: 'Force off (virtual-host)',
    bucket: 'Bucket',
    region: 'Region',
    endpointAws: 'Endpoint (leave blank for AWS)',
    endpoint: 'Endpoint',
    endpointHelp: 'Real AWS S3: leave blank. MinIO/Ceph: the gateway URL (http/https).',
    accessKey: 'Access Key ID',
    accessKeyHelp: 'Leave blank to keep the current key.',
    secretKey: 'Secret Access Key',
    secretKeyHelp: 'Write-only. Leave blank to keep the current secret.',
    directory: 'Directory',
    directoryHelp: 'Use a persistent, ideally shared (NFS) volume.',
  },
  queue: {
    title: 'Ingest queue',
    subtitle: 'Buffers batches between the collector and the workers.',
    driver: 'Driver',
    sqs: 'Amazon SQS',
    redis: 'Redis (no SQS; reuses your Redis)',
    sqsUrl: 'SQS queue URL',
    sqsHelp: 'Uses the same AWS region & credentials as storage.',
    redisUrl: 'Redis URL',
    redisHelp: 'The shared Redis connection (also used by live features).',
    redisKey: 'Redis list key',
  },
  search: {
    title: 'Search & analytics',
    subtitle: 'OpenSearch powers full-text search and funnels.',
    enabled: 'OpenSearch enabled',
    node: 'OpenSearch node URL',
    index: 'Index',
  },
  actions: {
    saving: 'Saving…',
    update: 'Update configuration',
    save: 'Save configuration',
    discard: 'Discard changes',
    saveFailed: 'Couldn’t save configuration.',
    unsaved: 'You have unsaved changes.',
    savedRestart: 'Saved. Restart services to apply.',
    allSaved: 'All changes saved. Edit any field to update.',
  },
  confirm: {
    title: 'Save without testing the connection?',
    changedPrefix: 'You changed',
    sections: { storage: 'Session storage', queue: 'Ingest queue', search: 'Search & analytics' } as Record<string, string>,
    notTestedThem: 'but haven’t run a successful Test connection for them.',
    notTestedIt: 'but haven’t run a successful Test connection for it.',
    warning:
      'Saving an unreachable configuration can stop session ingestion or make replays fail to load. Test first to be safe.',
    saveAnyway: 'Save anyway',
    goBack: 'Go back & test',
  },
  testing: 'Testing…',
  testConnection: 'Test connection',
};

const tr: typeof en = {
  title: 'Yapılandırma',
  needAdmin: 'Kurulum yapılandırmasını yönetmek için yönetici ya da sahip rolü gerekir.',
  subtitle:
    'ReplayOps’un veriyi nerede sakladığı ve işlediği. Değişiklikler veritabanına kaydedilir ve servisler yeniden başlatıldığında uygulanır.',
  restartPending:
    'Kaydedilen ayarlar çalışan servislerden farklı. Uygulanması için API, collector ve worker’ları yeniden başlatın.',
  testFailed: 'Test isteği başarısız oldu',
  storage: {
    title: 'Oturum depolama',
    subtitle: 'Kaydedilen oturum olaylarının tutulduğu yer.',
    driver: 'Sürücü',
    s3: 'S3 / S3 uyumlu (AWS, MinIO, Ceph…)',
    local: 'Yerel disk (on-prem / kapalı ortam)',
    preset: 'Sağlayıcı hazır ayarı',
    presetHelp: 'S3 uyumlu depolar arasında farklılaşan ayarları önceden doldurur.',
    presetAws: 'AWS S3',
    presetMinio: 'MinIO',
    presetCeph: 'Ceph (RadosGW)',
    presetOther: 'Diğer S3 uyumlu',
    pathStyle: 'Yol tabanlı adresleme',
    pathStyleHelp: 'AWS: sanal host · MinIO/Ceph: yol tabanlı',
    pathAuto: 'Otomatik (endpoint → yol tabanlı)',
    pathOn: 'Zorla açık (yol tabanlı)',
    pathOff: 'Zorla kapalı (sanal host)',
    bucket: 'Kova',
    region: 'Bölge',
    endpointAws: 'Endpoint (AWS için boş bırakın)',
    endpoint: 'Endpoint',
    endpointHelp: 'Gerçek AWS S3: boş bırakın. MinIO/Ceph: ağ geçidi URL’si (http/https).',
    accessKey: 'Erişim anahtarı kimliği',
    accessKeyHelp: 'Mevcut anahtarı korumak için boş bırakın.',
    secretKey: 'Gizli erişim anahtarı',
    secretKeyHelp: 'Yalnızca yazılır. Mevcut anahtarı korumak için boş bırakın.',
    directory: 'Dizin',
    directoryHelp: 'Kalıcı, tercihen paylaşımlı (NFS) bir birim kullanın.',
  },
  queue: {
    title: 'Veri alım kuyruğu',
    subtitle: 'Collector ile worker’lar arasında yığınları tamponlar.',
    driver: 'Sürücü',
    sqs: 'Amazon SQS',
    redis: 'Redis (SQS yok; mevcut Redis’inizi kullanır)',
    sqsUrl: 'SQS kuyruk URL’si',
    sqsHelp: 'Depolamayla aynı AWS bölgesini ve kimlik bilgilerini kullanır.',
    redisUrl: 'Redis URL’si',
    redisHelp: 'Paylaşılan Redis bağlantısı (canlı özellikler de bunu kullanır).',
    redisKey: 'Redis liste anahtarı',
  },
  search: {
    title: 'Arama ve analitik',
    subtitle: 'Tam metin arama ve hunileri OpenSearch çalıştırır.',
    enabled: 'OpenSearch etkin',
    node: 'OpenSearch düğüm URL’si',
    index: 'İndeks',
  },
  actions: {
    saving: 'Kaydediliyor…',
    update: 'Yapılandırmayı güncelle',
    save: 'Yapılandırmayı kaydet',
    discard: 'Değişiklikleri at',
    saveFailed: 'Yapılandırma kaydedilemedi.',
    unsaved: 'Kaydedilmemiş değişiklikleriniz var.',
    savedRestart: 'Kaydedildi. Uygulamak için servisleri yeniden başlatın.',
    allSaved: 'Tüm değişiklikler kaydedildi. Güncellemek için herhangi bir alanı düzenleyin.',
  },
  confirm: {
    title: 'Bağlantı test edilmeden kaydedilsin mi?',
    changedPrefix: 'Şunu değiştirdiniz:',
    sections: { storage: 'Oturum depolama', queue: 'Veri alım kuyruğu', search: 'Arama ve analitik' },
    notTestedThem: 'ama bunlar için başarılı bir Bağlantıyı test et çalıştırmadınız.',
    notTestedIt: 'ama bunun için başarılı bir Bağlantıyı test et çalıştırmadınız.',
    warning:
      'Erişilemeyen bir yapılandırmayı kaydetmek oturum alımını durdurabilir ya da kayıtların yüklenmemesine yol açabilir. Güvende olmak için önce test edin.',
    saveAnyway: 'Yine de kaydet',
    goBack: 'Geri dön ve test et',
  },
  testing: 'Test ediliyor…',
  testConnection: 'Bağlantıyı test et',
};

export const configuration = { en, tr };
